import { Component } from '@theme/component';

/** The visitor asked the browser to save data (Chrome's "Lite mode", Android data saver). */
function savesData() {
  const connection = /** @type {{ connection?: { saveData?: boolean } }} */ (navigator).connection;
  return Boolean(connection?.saveData);
}

/**
 * Product film player (blocks/product-film.liquid). Until the first play the poster shows with a large play
 * button and without browser controls; the button plays the film with sound and hands over to the native
 * controls (fullscreen, seeking, volume). When the film ends, the button returns for a replay.
 * The `controls` attribute stays in the HTML, so the film is playable before or without this script.
 * The film renders with `preload="none"` and its poster in `data-poster`, so it costs nothing far below the fold.
 * Once it comes within a viewport height, the poster is set and, unless the visitor saves data, an MP4 source (phones)
 * turns to `preload="metadata"` so a tap starts faster. The HLS stream (desktop) stays at `none`: Chrome buffers HLS
 * far beyond the metadata (~14 MB measured) even while paused.
 *
 * @typedef {{ video: HTMLVideoElement }} Refs
 * @extends {Component<Refs>}
 */
class ProductFilmComponent extends Component {
  requiredRefs = ['video'];

  /** @type {IntersectionObserver | undefined} */
  #approachObserver;

  /** Set when the sources changed under an untouched HLS film; `play()` reloads it first. */
  #stale = false;

  /**
   * Switching between the women's and men's product morphs the page in place (`variant-picker.js` morphs `main`
   * without the hooks that call `updatedCallback`): the sources change under the same element and the poster set by
   * script is dropped.
   */
  #rerenderObserver = new MutationObserver(() => this.#onRerender());

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;
    video.controls = false;
    video.addEventListener('play', this.#onPlay);
    video.addEventListener('ended', this.#onEnded);

    this.#approachObserver = new IntersectionObserver(this.#onApproach, { rootMargin: '100% 0px' });
    this.#approachObserver.observe(this);
    this.#rerenderObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src', 'poster', 'data-poster'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { video } = this.refs;
    video?.removeEventListener('play', this.#onPlay);
    video?.removeEventListener('ended', this.#onEnded);
    this.#approachObserver?.disconnect();
    this.#approachObserver = undefined;
    this.#rerenderObserver.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onApproach = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.#approachObserver?.disconnect();
    this.#approachObserver = undefined;
    this.#showPoster();

    const { video } = this.refs;
    if (!savesData() && video.preload === 'none' && /\.mp4(\?|$)/.test(video.currentSrc)) video.preload = 'metadata';
  };

  /**
   * Stops the film and reloads the element when its sources now belong to another film, and shows the poster again
   * once near. Chrome's native HLS player buffers the whole film on `load()` despite `preload="none"` (~14 MB
   * measured), so an untouched HLS film (desktop) reloads only when played; an MP4 or a film that already loaded
   * data reloads right away, which also brings its poster back.
   */
  #onRerender() {
    const { video } = this.refs;
    const sources = [...video.querySelectorAll('source')];
    if (video.currentSrc && !sources.some((source) => source.src === video.currentSrc)) {
      video.pause();
      this.#onEnded();
      if (/\.mp4(\?|$)/.test(video.currentSrc) || video.readyState > HTMLMediaElement.HAVE_NOTHING) video.load();
      else this.#stale = true;
    }
    if (!this.#approachObserver) this.#showPoster();
  }

  #showPoster() {
    const { video } = this.refs;
    if (video.dataset.poster && video.getAttribute('poster') !== video.dataset.poster) video.poster = video.dataset.poster;
  }

  play() {
    const { video } = this.refs;
    if (this.#stale) {
      this.#stale = false;
      video.load();
    }
    video.muted = false;
    // If the browser refuses, leave the visitor its own controls instead of a dead button.
    video.play().catch(() => this.#onPlay());
  }

  #onPlay = () => {
    this.setAttribute('data-started', '');
    this.refs.video.controls = true;
  };

  #onEnded = () => {
    this.removeAttribute('data-started');
    this.refs.video.controls = false;
  };
}

if (!customElements.get('product-film-component')) {
  customElements.define('product-film-component', ProductFilmComponent);
}
